import { useEffect, useMemo, useState } from 'react';
import { useAlertsStore } from '@/stores/alerts-store';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime } from '@/utils/date';
import { ALERT_CONFIG, TICKET_STATUS_LABELS } from '@asi/shared';
import { format, differenceInMinutes, differenceInSeconds } from 'date-fns';
import { es } from 'date-fns/locale';
import { MapPin, Navigation, User, Clock, Shield, MessageSquare, Image, Mic, FileText, ChevronRight, Video, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/services/supabase';
import type { Ticket, TicketWithRelations, AlertType, TicketStatus } from '@asi/shared';

/** Sign a storage path from the evidence bucket; pass-through full URLs. */
async function resolveMediaUrl(value: string | null): Promise<string | null> {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const { data, error } = await supabase.storage.from('evidence').createSignedUrl(value, 3600);
  if (error) { console.warn('[TicketsPage] sign url failed', value, error.message); return null; }
  return data?.signedUrl ?? null;
}

const TYPE_FILTERS: AlertType[] = ['SOS', 'FIRE', 'MEDICAL', 'CALL'];

type TabView = 'pending' | 'inProgress' | 'history';

const TAB_CONFIG: Record<TabView, { label: string; statuses: TicketStatus[]; emptyMessage: string }> = {
  pending: {
    label: 'Pendientes',
    statuses: ['PENDING'],
    emptyMessage: 'No hay alertas pendientes',
  },
  inProgress: {
    label: 'En Proceso',
    statuses: ['ASSIGNED', 'EN_ROUTE', 'ON_SCENE', 'IN_PROGRESS'],
    emptyMessage: 'No hay alertas en proceso',
  },
  history: {
    label: 'Historial',
    statuses: ['RESOLVED', 'CLOSED', 'ESCALATED', 'CANCELLED'],
    emptyMessage: 'No hay alertas finalizadas',
  },
};

export function TicketsPage() {
  const { tickets, selectedTicket, loading, fetchTickets, selectTicket, subscribeToTickets } =
    useAlertsStore();
  const [activeTab, setActiveTab] = useState<TabView>('pending');
  const [typeFilter, setTypeFilter] = useState<AlertType | 'ALL'>('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchTickets();
    const unsub = subscribeToTickets();
    return unsub;
  }, [fetchTickets, subscribeToTickets]);

  const tabStatuses = TAB_CONFIG[activeTab].statuses;

  const filtered = tickets
    .filter((t) => {
      if (!tabStatuses.includes(t.status)) return false;
      if (typeFilter !== 'ALL' && t.type !== typeFilter) return false;
      if (search && !t.ticket_number.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()); // Most recent first

  return (
    <div className="flex h-full">
      {/* Ticket List */}
      <div className={`flex-1 flex flex-col ${selectedTicket ? 'max-w-[55%]' : ''}`}>
        <div className="p-6 pb-4">
          <h2 className="text-2xl font-bold text-foreground mb-4">Gestión de Alertas</h2>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 p-1 bg-muted rounded-lg">
            {(Object.keys(TAB_CONFIG) as TabView[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {TAB_CONFIG[tab].label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Input
              type="text"
              placeholder="Buscar por número..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-auto"
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as AlertType | 'ALL')}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="ALL">Todos los tipos</option>
              {TYPE_FILTERS.map((t) => (
                <option key={t} value={t}>{ALERT_CONFIG[t].label}</option>
              ))}
            </select>
          </div>

          <p className="text-muted-foreground text-sm">{filtered.length} alertas</p>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  isSelected={selectedTicket?.id === ticket.id}
                  onClick={() => selectTicket(ticket.id)}
                />
              ))}
              {filtered.length === 0 && (
                <p className="text-muted-foreground/60 text-center py-16">{TAB_CONFIG[activeTab].emptyMessage}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedTicket && <TicketDetail ticket={selectedTicket} onClose={() => useAlertsStore.setState({ selectedTicket: null })} />}
    </div>
  );
}

function TicketRow({ ticket, isSelected, onClick }: { ticket: Ticket; isSelected: boolean; onClick: () => void }) {
  const config = ALERT_CONFIG[ticket.type];

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-xl cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/10 dark:bg-accent/10 border-2 border-primary/50 dark:border-accent/50 shadow-sm' 
          : 'bg-card border border-border hover:bg-muted hover:border-muted-foreground/20'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Type Indicator */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 relative"
          style={{ backgroundColor: config.color + '20' }}
        >
          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: config.color }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-foreground font-semibold text-base">{ticket.ticket_number}</span>
            <StatusBadge status={ticket.status} />
            {ticket.priority !== 'MEDIUM' && <StatusBadge status={ticket.priority} />}
          </div>

          {/* Type Label */}
          <p className="text-muted-foreground text-sm font-medium mb-2">
            {config.label}
            {(ticket as Ticket & { source?: string }).source === 'ble_button' && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200 dark:border-violet-700">
                📟 Botón Físico
              </span>
            )}
          </p>

          {/* Location */}
          {ticket.address && (
            <div className="flex items-center gap-1.5 text-muted-foreground/80 text-xs mb-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{ticket.address}</span>
            </div>
          )}

          {/* Metadata Row */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground/70 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatDateTime(ticket.created_at)}</span>
            </div>
            {ticket.patrol_id && (
              <div className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                <span>Patrullero asignado</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}min`;
}

function TicketDetail({ ticket, onClose }: { ticket: TicketWithRelations; onClose: () => void }) {
  // Signed media URLs keyed by message.id or evidence.id
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    setSignedUrls({});
    const items: Array<{ key: string; path: string | null }> = [
      ...(ticket.messages ?? []).filter(m => m.file_url && m.type !== 'text' && m.type !== 'system' && m.type !== 'location')
        .map(m => ({ key: `msg-${m.id}`, path: m.file_url })),
      ...(ticket.evidence ?? []).map(ev => ({ key: `ev-${ev.id}`, path: ev.file_url })),
    ];
    if (items.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries: Array<[string, string]> = [];
      for (const { key, path } of items) {
        const url = await resolveMediaUrl(path);
        if (url) entries.push([key, url]);
      }
      if (cancelled) return;
      setSignedUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [ticket.id]);
  // Merged chronological timeline: system messages + ticket logs
  const timeline = useMemo(() => {
    const sysMsgs = (ticket.messages ?? [])
      .filter((m) => m.type === 'system')
      .map((m) => ({ id: `msg-${m.id}`, label: m.body ?? '', ts: m.created_at, kind: 'status' as const }));
    const logEntries = (ticket.logs ?? [])
      .map((l) => ({ id: `log-${l.id}`, label: l.action, ts: l.created_at, kind: 'log' as const }));
    // Show logs only if no system messages exist (backwards compat for old tickets)
    const entries = sysMsgs.length > 0 ? sysMsgs : logEntries;
    return entries.sort((a, b) => a.ts.localeCompare(b.ts));
  }, [ticket.messages, ticket.logs]);

  const responseTimeSec = ticket.assigned_at
    ? differenceInSeconds(new Date(ticket.assigned_at), new Date(ticket.created_at))
    : null;
  const closedAt = ticket.resolved_at ?? (ticket.status === 'CLOSED' || ticket.status === 'CANCELLED' ? ticket.updated_at : null);
  const totalDurationSec = closedAt
    ? differenceInSeconds(new Date(closedAt), new Date(ticket.created_at))
    : null;

  const chatMessages = (ticket.messages ?? []).filter((m) => m.type !== 'system');

  return (
    <div className="w-[45%] border-l border-border bg-card flex flex-col">
      {/* Sticky header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 z-10 bg-card">
        <div>
          <h3 className="text-lg font-bold text-foreground leading-tight">{ticket.ticket_number}</h3>
          <p className="text-muted-foreground text-xs mt-0.5">
            {ALERT_CONFIG[ticket.type].label}
            {(ticket as Ticket & { source?: string }).source === 'ble_button' && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200 dark:border-violet-700">
                📟 Botón Físico
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <StatusBadge status={ticket.status} size="sm" />
            <StatusBadge status={ticket.priority} size="sm" />
          </div>
          <button onClick={onClose} className="text-muted-foreground/60 hover:text-foreground text-2xl leading-none">&times;</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Key metrics */}
        {(responseTimeSec !== null || totalDurationSec !== null) && (
          <div className="grid grid-cols-2 gap-3">
            {responseTimeSec !== null && (
              <div className="bg-muted rounded-xl p-3">
                <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Tiempo de respuesta</p>
                <p className="text-foreground font-semibold text-lg">{fmtDuration(responseTimeSec)}</p>
              </div>
            )}
            {totalDurationSec !== null && (
              <div className="bg-muted rounded-xl p-3">
                <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Duración total</p>
                <p className="text-foreground font-semibold text-lg">{fmtDuration(totalDurationSec)}</p>
              </div>
            )}
          </div>
        )}

        {/* Client & patrol */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted rounded-xl p-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Cliente</p>
              <p className="text-foreground text-sm font-medium truncate">{ticket.client?.full_name ?? '—'}</p>
            </div>
          </div>
          <div className="bg-muted rounded-xl p-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
              <Shield className="w-4 h-4 text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Patrullero</p>
              <p className="text-foreground text-sm font-medium truncate">{ticket.patrol?.full_name ?? 'Sin asignar'}</p>
            </div>
          </div>
        </div>

        {/* Key timestamps */}
        <div className="bg-muted rounded-xl p-4">
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-3">Tiempos clave</p>
          <div className="space-y-1.5">
            {([
              { label: 'Creado',     value: ticket.created_at },
              { label: 'Asignado',   value: ticket.assigned_at },
              { label: 'Respondido', value: ticket.responded_at },
              { label: 'Finalizado', value: ticket.resolved_at },
            ] as { label: string; value: string | null }[]).map(({ label, value }) => value && (
              <div key={label} className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="text-foreground font-mono text-xs">
                  {format(new Date(value), 'dd/MM/yy HH:mm:ss', { locale: es })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Location */}
        {ticket.address && (
          <div className="bg-muted rounded-xl p-4">
            <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Ubicación</p>
            <p className="text-foreground text-sm flex items-start gap-1.5 mb-2">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-red-400 shrink-0" />
              {ticket.address}
            </p>
            {ticket.location && (
              <a
                href={`https://www.google.com/maps?q=${(ticket.location as any).coordinates?.[1]},${(ticket.location as any).coordinates?.[0]}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:underline"
              >
                <Navigation className="w-3 h-3" /> Abrir en Google Maps
              </a>
            )}
          </div>
        )}

        {/* Notes */}
        {(ticket.description || ticket.resolution_notes) && (
          <div className="bg-muted rounded-xl p-4 space-y-3">
            {ticket.description && (
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Descripción</p>
                <p className="text-foreground text-sm">{ticket.description}</p>
              </div>
            )}
            {ticket.resolution_notes && (
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Notas de resolución</p>
                <p className="text-foreground text-sm">{ticket.resolution_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Evidence */}
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-3">
            Evidencia ({ticket.evidence?.length ?? 0})
          </p>
          {(ticket.evidence?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {ticket.evidence.map((ev) => {
                const url = signedUrls[`ev-${ev.id}`];
                return (
                  <div key={ev.id} className="group relative aspect-square rounded-xl overflow-hidden bg-muted border border-border hover:border-primary/50 transition-colors">
                    {!url ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : ev.type === 'PHOTO' ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                        <img src={url} alt="evidencia" className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
                      </a>
                    ) : ev.type === 'VIDEO' ? (
                      <video src={url} controls className="w-full h-full object-cover" preload="metadata" />
                    ) : ev.type === 'AUDIO' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-2">
                        <Mic className="w-5 h-5 text-muted-foreground" />
                        <audio src={url} controls className="w-full" />
                      </div>
                    ) : (
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground/70 hover:text-foreground">
                        <FileText className="w-5 h-5" />
                        <span className="text-[10px] uppercase">{ev.type}</span>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground/60 text-sm">Sin evidencia adjunta</p>
          )}
        </div>

        {/* Chronological timeline (status changes + logs) */}
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-3">
            Seguimiento ({timeline.length})
          </p>
          {timeline.length === 0 ? (
            <p className="text-muted-foreground/60 text-sm">Sin actividad registrada</p>
          ) : (
            <ol className="relative border-l-2 border-border ml-2">
              {timeline.map((entry, idx) => (
                <li key={entry.id} className="ml-5 pb-4 last:pb-0">
                  <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-card ${
                    entry.kind === 'status' ? 'bg-primary dark:bg-accent' : 'bg-muted-foreground/40'
                  }`}>
                    {entry.kind === 'status'
                      ? <Shield className="w-2 h-2 text-white" />
                      : <Clock className="w-2 h-2 text-white" />}
                  </span>
                  <p className={`text-sm leading-snug ${
                    entry.kind === 'status' ? 'text-foreground font-medium' : 'text-muted-foreground'
                  }`}>{entry.label}</p>
                  <p className="text-[11px] text-muted-foreground/60 mt-0.5">
                    {format(new Date(entry.ts), 'dd/MM/yy HH:mm:ss', { locale: es })}
                    {idx > 0 && (() => {
                      const prev = timeline[idx - 1];
                      const diffMin = differenceInMinutes(new Date(entry.ts), new Date(prev.ts));
                      if (diffMin < 1) return null;
                      return <span className="ml-2 text-muted-foreground/40">(+{diffMin} min)</span>;
                    })()}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Chat messages (non-system) */}
        {chatMessages.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wider mb-3">
              Mensajes del chat ({chatMessages.length})
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {chatMessages.map((m) => {
                const isStaff = m.sender?.role === 'operator' || m.sender?.role === 'admin' || m.sender?.role === 'super_admin';
                return (
                  <div key={m.id} className={`flex gap-2 ${isStaff ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <MessageSquare className="w-3 h-3 text-muted-foreground" />
                    </div>
                    <div className={`max-w-[75%] rounded-xl px-3 py-2 ${isStaff ? 'bg-primary/10 dark:bg-accent/10' : 'bg-muted'}`}>
                      <p className="text-[10px] text-muted-foreground/70 mb-0.5">
                        {m.sender?.full_name ?? (isStaff ? 'Operador' : 'Cliente')}
                      </p>
                      {m.type === 'text' && <p className="text-sm text-foreground break-words">{m.body}</p>}
                      {m.type === 'image' && (() => {
                        const url = signedUrls[`msg-${m.id}`];
                        return url
                          ? <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="foto" className="rounded-lg max-h-48 object-cover" loading="lazy" /></a>
                          : <span className="flex items-center gap-1 text-xs opacity-70"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</span>;
                      })()}
                      {m.type === 'video' && (() => {
                        const url = signedUrls[`msg-${m.id}`];
                        return url
                          ? <video src={url} controls className="rounded-lg max-w-full max-h-48" preload="metadata" />
                          : <span className="flex items-center gap-1 text-xs opacity-70"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</span>;
                      })()}
                      {m.type === 'audio' && (() => {
                        const url = signedUrls[`msg-${m.id}`];
                        return url
                          ? <audio src={url} controls className="max-w-full" />
                          : <span className="flex items-center gap-1 text-xs opacity-70"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</span>;
                      })()}
                      {m.type === 'location' && (
                        <span className="flex items-center gap-1 text-xs opacity-90"><MapPin className="w-3 h-3" /> {m.body ?? 'Ubicación'}</span>
                      )}
                      <p className="text-[10px] text-muted-foreground/50 mt-1 text-right">
                        {format(new Date(m.created_at), 'HH:mm', { locale: es })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
